<template>
  <transition name="appointment-celebration-panel">
    <div v-if="value" class="appointment-celebration-panel-shell">
      <div class="appointment-celebration-panel-backdrop" @click="closePanel" />

      <aside class="appointment-celebration-panel">
        <div class="appointment-celebration-panel__header">
          <div class="appointment-celebration-panel__title">
            <span class="appointment-celebration-panel__icon">
              <i class="fas fa-check-circle" />
            </span>
            <span>Registrar Celebración</span>
          </div>

          <button
            type="button"
            class="appointment-celebration-panel__close"
            @click="closePanel"
          >
            <i class="fal fa-times-circle" />
          </button>
        </div>

        <div class="appointment-celebration-panel__body">
          <div class="appointment-celebration-panel__question">
            ¿El padre/madre asistió a la reunión del {{ formattedDate }}?
          </div>

          <div class="appointment-celebration-panel__options">
            <custom-radio
              v-model="selectedOption"
              name="appointmentCelebrationOption"
              :options="celebrationOptions"
              is-inline
              @input="handleOptionChange"
            />
          </div>

          <div
            v-if="validationError"
            class="appointment-celebration-panel__error"
          >
            <i class="fas fa-times-circle" />
            <span>Campo requerido</span>
          </div>

          <div
            v-if="isCelebratedSelected"
            class="appointment-celebration-panel__question mt-4"
          >
            ¿Se Celebró la Minuta Mediante Sistema?
          </div>

          <div
            v-if="isCelebratedSelected"
            class="appointment-celebration-panel__options"
          >
            <custom-radio
              v-model="selectedMinuteCelebratedBySystem"
              name="appointmentMinuteCelebrationOption"
              :options="minuteCelebrationOptions"
              is-inline
              @input="handleMinuteCelebrationChange"
            />
          </div>

          <div
            v-if="isCelebratedSelected && minuteCelebrationValidationError"
            class="appointment-celebration-panel__error"
          >
            <i class="fas fa-times-circle" />
            <span>Campo requerido</span>
          </div>

          <div
            v-if="isCelebratedSelected && selectedMinuteCelebratedBySystem === 'yes'"
            class="appointment-celebration-panel__minute-field"
          >
            <custom-drop-down
              v-model="selectedMinuteId"
              :options="minuteOptions"
              label="Seleccione Minuta:"
              fieldkey="id"
              fieldname="textSelection"
              placeholder="Seleccione"
              :disabled="loadingMinutes"
              :error="minuteValidationError"
              :custom-error-msg="minuteValidationError"
              @input="minuteValidationError = ''"
            />
          </div>
        </div>

        <div class="appointment-celebration-panel__footer">
          <action-button
            class="appointment-celebration-panel__button"
            i="fa fa-times-circle text-danger mr-n1 pr-2"
            text="Cancelar"
            variant="outline-danger"
            @click="closePanel"
          />
          <action-button
            class="appointment-celebration-panel__button"
            i="fa fa-save text-white mr-n1 pr-2"
            text="Guardar"
            variant="success"
            @click="save"
          />
        </div>
      </aside>
    </div>
  </transition>
</template>

<script>
import moment from 'moment';
import ActionButton from 'ActionButton';
import CustomRadio from '@/components/common/CustomRadio';
import CustomDropDown from 'custom-drop-down';
import alert from '@/mixin/alert.mixin';
import compuAppointmentService from '@/modules/compu-appointment/api';

export default {
  name: 'AppointmentCelebrationPanel',
  components: {
    ActionButton,
    CustomRadio,
    CustomDropDown,
  },
  mixins: [alert],
  props: {
    value: { type: Boolean, default: false },
    appointment: { type: Object, default: null },
  },
  data() {
    return {
      selectedOption: null,
      selectedMinuteCelebratedBySystem: null,
      selectedMinuteId: null,
      minuteOptions: [],
      hasLoadedMinuteOptions: false,
      celebrationOptions: [
        {
          id: 'celebrated',
          value: 'celebrated',
          label: 'Celebrada',
        },
        {
          id: 'notCelebrated',
          value: 'notCelebrated',
          label: 'No celebrada',
        },
      ],
      minuteCelebrationOptions: [
        {
          id: 'yes',
          value: 'yes',
          label: 'Sí',
        },
        {
          id: 'no',
          value: 'no',
          label: 'No',
        },
      ],
      validationError: false,
      minuteCelebrationValidationError: false,
      minuteValidationError: '',
      loadingMinutes: false,
      saving: false,
    };
  },
  computed: {
    formattedDate() {
      if (!this.appointment?.appointmentDate) {
        return '-';
      }

      return moment(this.appointment.appointmentDate)
        .locale('es')
        .format('DD/MMM./YYYY')
        .replace('..', '.');
    },
    selectedStatusLabel() {
      return this.selectedOption === 'celebrated'
        ? 'Celebrada'
        : 'No Celebrada';
    },
    isCelebratedSelected() {
      return this.selectedOption === 'celebrated';
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.resetForm();
      }
    },
  },
  methods: {
    resetForm() {
      this.selectedOption = null;
      this.selectedMinuteCelebratedBySystem = null;
      this.selectedMinuteId = null;
      this.minuteOptions = [];
      this.hasLoadedMinuteOptions = false;
      this.validationError = false;
      this.minuteCelebrationValidationError = false;
      this.minuteValidationError = '';
      this.saving = false;
    },
    handleOptionChange() {
      this.validationError = false;

      if (!this.isCelebratedSelected) {
        this.selectedMinuteCelebratedBySystem = null;
        this.selectedMinuteId = null;
        this.minuteCelebrationValidationError = false;
        this.minuteValidationError = '';
      }
    },
    handleMinuteCelebrationChange() {
      this.minuteCelebrationValidationError = false;
      this.minuteValidationError = '';

      if (this.selectedMinuteCelebratedBySystem !== 'yes') {
        this.selectedMinuteId = null;
        return;
      }

      if (!this.hasLoadedMinuteOptions) {
        this.fetchCompletedMinutes();
      }
    },
    async fetchCompletedMinutes() {
      if (this.loadingMinutes) {
        return;
      }

      if (!this.appointment?.id) {
        this.minuteOptions = [];
        return;
      }

      try {
        this.loadingMinutes = true;
        const { data } = await compuAppointmentService.fetchCompletedMinutes(
          this.appointment.id
        );
        this.minuteOptions = Array.isArray(data) ? data : data?.data || [];
        this.hasLoadedMinuteOptions = true;
      } catch {
        this.minuteOptions = [];
      } finally {
        this.loadingMinutes = false;
      }
    },
    closePanel() {
      if (this.saving) {
        return;
      }

      this.$emit('input', false);
    },
    async save() {
      if (!this.validateForm()) {
        return;
      }

      const attended = this.selectedOption === 'celebrated';
      const title = `Marcar cita como ${this.selectedStatusLabel}`;
      const message = attended
        ? `¿Confirma que el padre/madre asistió a la reunión del ${this.formattedDate}? Esta acción actualizará el estatus a ${this.selectedStatusLabel}.`
        : `¿Confirma que el padre/madre no asistió a la reunión del ${this.formattedDate}? Esta acción actualizará el estatus a ${this.selectedStatusLabel}.`;

      let alertResponse = await this.ShowCancelModal(
        title,
        message,
        'Confirmar',
        'Cancelar'
      );

      if (!alertResponse) {
        return;
      }

      try {
        this.saving = true;
        await compuAppointmentService.registerCelebration(this.appointment.id, {
          isCelebrated: this.selectedOption === 'celebrated',
          isMinuteCelebratedBySystem:
            this.isCelebratedSelected &&
            this.selectedMinuteCelebratedBySystem === 'yes',
          compuMinuteId:
            this.isCelebratedSelected &&
            this.selectedMinuteCelebratedBySystem === 'yes'
              ? this.getSelectedMinuteId()
              : null,
        });

        this.ShowToast(
          'Proceso Exitoso',
          'La cita ha sido actualizada exitosamente.',
          'success'
        );
        this.$emit('saved');
        this.$emit('input', false);
      } catch (error) {
        const errorMessage =
          typeof error?.response?.data === 'string'
            ? error.response.data
            : 'No se pudo actualizar el estatus de la cita.';

        this.ShowToast('Error', errorMessage, 'error');
      } finally {
        this.saving = false;
      }
    },
    getSelectedMinuteId() {
      const selectedMinute = this.selectedMinuteId;

      if (selectedMinute && typeof selectedMinute === 'object') {
        return selectedMinute.id ? Number(selectedMinute.id) : null;
      }

      return selectedMinute ? Number(selectedMinute) : null;
    },
    validateForm() {
      this.validationError = !this.selectedOption;
      this.minuteCelebrationValidationError =
        this.isCelebratedSelected && !this.selectedMinuteCelebratedBySystem;
      this.minuteValidationError = '';

      if (
        this.isCelebratedSelected &&
        this.selectedMinuteCelebratedBySystem === 'yes' &&
        !this.selectedMinuteId
      ) {
        this.minuteValidationError = 'Debe seleccionar la minuta.';
      }

      return (
        !this.validationError &&
        !this.minuteCelebrationValidationError &&
        !this.minuteValidationError
      );
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables/_colors.scss';

.appointment-celebration-panel-shell {
  position: fixed;
  inset: 0;
  z-index: 1040;
}

.appointment-celebration-panel-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 30, 32, 0.42);
  backdrop-filter: blur(2px);
}

.appointment-celebration-panel {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  width: min(720px, 100vw);
  height: 100vh;
  background: #fff;
  box-shadow: -12px 0 24px rgba(0, 0, 0, 0.12);
}

.appointment-celebration-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 26px;
  border-bottom: 1px solid #e2e2e2;
}

.appointment-celebration-panel__title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  color: #11291b;
}

.appointment-celebration-panel__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: #fff;
  background: #ff7058;
  box-shadow: 0 2px 10px rgba(255, 112, 88, 0.25);
}

.appointment-celebration-panel__close {
  border: 0;
  background: transparent;
  color: #ff7058;
  cursor: pointer;
}

.appointment-celebration-panel__body {
  flex: 1;
  padding: 22px 26px;
}

.appointment-celebration-panel__question {
  margin-bottom: 10px;
  color: #11291b;
}

.appointment-celebration-panel__options ::v-deep .col-12 {
  padding-left: 0 !important;
}

.appointment-celebration-panel__error {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: 8px;
  color: #ff4438;
  font-size: 14px;
}

.appointment-celebration-panel__minute-field {
  margin-top: 14px;
}

.appointment-celebration-panel__footer {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding: 18px 26px;
  border-top: 1px solid #e2e2e2;
}

.appointment-celebration-panel__button,
.appointment-celebration-panel__button .btn,
.appointment-celebration-panel__button button {
  width: 100%;
}
</style>
